import {
  collection,
  getDocs,
  doc,
  query,
  where,
  orderBy,
  Timestamp,
  getDoc,
  updateDoc,
  addDoc
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { WorkAssignmentEntry } from './CalendarTypes';

export class WorkAssignmentService {
  static async fetchWorkAssignmentEntries(
    companyId: string,
    selectedDate: string,
    selectedUserId: string
  ): Promise<WorkAssignmentEntry[]> {
    if (!companyId || !selectedDate || !selectedUserId) return [];

    try {
      const workAssignmentsRef = collection(db, `companies/${companyId}/workAssignments`);
      const selectedDateStart = new Date(selectedDate);
      const selectedDateEnd = new Date(selectedDate);
      selectedDateEnd.setHours(23, 59, 59, 999);
      
      // Filter work assignments by selected user and date range (both done and pending)
      const workAssignmentsQuery = query(
        workAssignmentsRef,
        where('userId', '==', selectedUserId),
        where('dueDate', '>=', Timestamp.fromDate(selectedDateStart)),
        where('dueDate', '<=', Timestamp.fromDate(selectedDateEnd)),
        orderBy('dueDate', 'asc')
      );

      const snapshot = await getDocs(workAssignmentsQuery);
      const workAssignmentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        entryType: 'workAssignment'
      })) as WorkAssignmentEntry[];
      
      // Fetch user details for each work assignment
      const enhancedWorkAssignments = await Promise.all(workAssignmentsData.map(async (workAssignment) => {
        try {
          // Get assigned by user name using assignedBy
          if (workAssignment.assignedBy) {
            const userRef = doc(db, 'users', workAssignment.assignedBy);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
              const userData = userDoc.data();
              workAssignment.assignedByName = userData.name || userData.email || 'Unknown User';
            }
          }
          
          // Get assigned to user name using userId
          if (workAssignment.userId) {
            const userRef = doc(db, 'users', workAssignment.userId);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
              const userData = userDoc.data();
              workAssignment.userName = userData.name || userData.email || 'Unknown User';
            }
          }
          
          return workAssignment;
        } catch (error) {
          console.error('Error fetching details for work assignment:', error);
          return workAssignment;
        }
      }));

      return enhancedWorkAssignments;
    } catch (error) {
      console.error('Error fetching work assignment entries:', error);
      return [];
    }
  }

  static async markWorkAssignmentAsDone(
    companyId: string,
    workAssignmentId: string
  ): Promise<void> {
    try {
      // Update the work assignment to mark as done
      const workAssignmentRef = doc(db, `companies/${companyId}/workAssignments`, workAssignmentId);
      await updateDoc(workAssignmentRef, {
        isDone: true,
        completedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error marking work assignment as done:', error);
      throw error;
    }
  }

  static async createWorkAssignment(
    companyId: string,
    userId: string,
    assignedBy: string,
    workDescription: string,
    dueDate: Date
  ): Promise<void> {
    try {
      const workAssignmentsRef = collection(db, `companies/${companyId}/workAssignments`);
      
      await addDoc(workAssignmentsRef, {
        userId,
        assignedBy,
        workDescription,
        dueDate: Timestamp.fromDate(dueDate),
        createdAt: Timestamp.now(),
        isDone: false
      });
    } catch (error) {
      console.error('Error creating work assignment:', error);
      throw error;
    }
  }
}
